<template>
  <div class="container-fluid p-0 m-0">
    <a href="#main-content" class="skip-link">Skip to main content</a>
    <NavBar class="mb-0" :formId="formId" :isAdmin="isAdmin" @logoff="logoff" />
    <div class="sticky-tabs shadow-sm">
      <PageTabs v-if="user" @switchPage="switchPage" :currentPage="currentPage" />
    </div>
    <div class="container main" id="main-content">
      <router-view :user="user" :isAdmin="isAdmin" :canUsePdfParser="canUsePdfParser" :canManageOrderLines="canManageOrderLines" @showToast="showToast" ref="routerView" />
    </div>
    <div id="liveToast" :class="toastColor" class="toast toast-container-top-left align-items-center text-white border-0 ms-4" role="alert"
      aria-live="assertive" aria-atomic="true">
      <div class="d-flex">
        <div class="toast-body">
          {{ toastMessage }}
        </div>
        <button type="button" class="btn-close btn-close-white me-2 m-auto" data-bs-dismiss="toast"
          aria-label="Close"></button>
      </div>
    </div>
    <ToastStack ref="toastStack" />
  </div>
</template>

<script>
import NavBar from './components/NavBar.vue'
import PageTabs from './components/PageTabs.vue'
import ToastStack from './components/ToastStack.vue'
import ServiceFactory from './services/ServiceFactory'
import { watch } from 'vue'
import localDB from './utils/localDB';
import api from './services/axiosConfig';
import fetchInvoicesAction from "@/services/FetchInvoicesAction";
import fetchOrdersAction from "@/services/FetchOrdersAction";
import toastService from './services/ToastService';

export default {
  name: 'App',
  components: {
    NavBar, PageTabs, ToastStack
  },
  data() {
    return {
      formId: 'app-form',
      currentPage: 'orders',
      user: null,
      toastMessage: '',
      toastColor: 'bg-primary',
      oAuthService: null,
      orderService: null,
      invoiceService: null,
      customerService: null,
      pdfParsingService: null,
      isAdmin: false,
      userRoles: []
    }
  },
  computed: {
    canUsePdfParser() {
      return this.isAdmin || (this.userRoles && this.userRoles.includes('pdf_parser'));
    },
    canManageOrderLines() {
      return this.isAdmin || (this.userRoles && this.userRoles.includes('pdf_parser'));
    }
  },
  watch: {
    // Update de geselecteerde tab als de route verandert
    $route(to) {
      this.currentPage = to.name;
    }
  },
  methods: {
    showToast(message) {
      const color = message[1]
      this.toastColor = color ? color : 'bg-primary';
      this.toastMessage = message[0]
      this.$nextTick(() => {
        const toastLiveExample = document.getElementById('liveToast')
        const toastBootstrap = window.bootstrap.Toast.getOrCreateInstance(toastLiveExample)
        toastBootstrap.show()
      })

    },
    switchPage(pageName) {
      switch (pageName) {
        case 'orders':
          this.$router.push('/orders');
          break;
        case 'orderbevestigingen':
          this.$router.push('/orderBevestigingen')
          break;
        case 'invoices':
          this.$router.push('/invoices')
          break;
        case 'customers':
          this.$router.push('/customers')
          break;
        default:
          throw new Error(`can't find pageName: ${pageName}`)
      }
      this.currentPage = pageName
    },
    async loadCurrentPage() {
      const currentPageStorage = await localDB.getItem('currentPage');
      if (currentPageStorage) {
        this.currentPage = JSON.parse(currentPageStorage);
      }
      this.switchPage(this.currentPage)

      watch(() => this.currentPage, async (newValue) => {
        await localDB.setItem('currentPage', JSON.stringify(newValue));
      }, { deep: true });
    },
    async retrieveAccessToken(code) {
      const data = await this.oAuthService.sendAuthCodeToServer(code)
      if(!data) {
        this.showToast(['Failed to login to Azure authorisation server', 'bg-danger'])
        return
      }
      this.user = { token: data.token, is_admin: data.is_admin }

      await localDB.setItem('is_admin', data.is_admin);
      await localDB.setItem('token', data.token);

      const roles = data.roles || [];
      await localDB.setItem('user_roles', JSON.stringify(roles));
      this.userRoles = roles;

      this.isAdmin = data.is_admin;

      api.defaults.headers.common['Authorization'] = `Bearer ${data.token}`;
    },
    invalidateToken() {
      localDB.removeItem('token');
      localDB.removeItem('is_admin');
      localDB.removeItem('user_roles');
      this.user = null;
      this.isAdmin = false;
      this.userRoles = [];
    },
    logoff() {
      this.$router.push('/login')
      this.user = null
      this.isAdmin = false;
      this.userRoles = [];
      localDB.removeItem('token');
      localDB.removeItem('is_admin');
      localDB.removeItem('user_roles');
    },
    setSelectedTabFromRoute() {
      const route = this.$route; // Huidige route object
      if(route.path === '/') {
        this.currentPage = 'orders'
        return
      } else if(route.path) {
        this.currentPage = route.path.substring(1); // Gebruik de path van de route als geselecteerde tab
      }
    },
    async checkAdminStatus() {
      this.isAdmin = await localDB.getItem('is_admin');
      return this.isAdmin;
    }
  },

  created() {
    this.orderService = ServiceFactory.getService('OrderService')
    this.invoiceService = ServiceFactory.getService('InvoiceService')
    this.customerService = ServiceFactory.getService('CustomerService')
    this.oAuthService = ServiceFactory.getService('OAuth2AzureService')
    this.pdfParsingService = ServiceFactory.getService('PdfParsingService')
    this.fetchInvoicesAction = fetchInvoicesAction;
    this.fetchOrdersAction = fetchOrdersAction;
    this.orderService.addEventListener('unauthorized', () => this.$router.push('/login'))
    this.invoiceService.addEventListener('unauthorized', () => this.$router.push('/login'))
    this.customerService.addEventListener('unauthorized', () => this.$router.push('/login'))
    this.pdfParsingService.addEventListener('unauthorized', () => this.$router.push('/login'))
    this.fetchInvoicesAction.addEventListener('unauthorized', () => this.$router.push('/login'))
    this.fetchOrdersAction.addEventListener('unauthorized', () => this.$router.push('/login'))
    this.orderService.addEventListener('orderLoadedFailed', (event) => {
      this.showToast([event.detail.message, 'bg-danger'])
    })
  },
  async mounted() {
    // Initialiseer ToastService met de ToastStack ref
    toastService.setToastStackRef(this.$refs.toastStack);
    
    // Initialiseer isAdmin uit localDB (fix race condition)
    await this.checkAdminStatus();
    
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    setTimeout(this.setSelectedTabFromRoute, 200)

    if (code) {
      await this.retrieveAccessToken(code);
      this.$router.push('/orders')
      setTimeout(this.setSelectedTabFromRoute, 200)
    } else {
      const token = await localDB.getItem('token');
      const is_admin = await localDB.getItem('is_admin');
      if (token) {
        this.user = { token, is_admin }
        this.isAdmin = is_admin;
        const storedRoles = await localDB.getItem('user_roles');
        this.userRoles = storedRoles ? JSON.parse(storedRoles) : [];
      } else {
        this.$router.push('/login')
      }
    }
  }
}
</script>

<style>
body {
  /* ivm met fixed-top navbar + sticky tabs */
  padding-top: 130px;
}

.sticky-tabs {
  position: fixed;
  top: 76px; /* Hoogte van navbar */
  left: 0;
  right: 0;
  z-index: 1000;
  background: white;
  padding: 0.25rem 0;
}

/* Main container moet hoogte beperken voor flex children */
.container.main {
  max-width: 100%;
  padding: 0 1rem;
}

@media (max-width: 991.98px) {
  body {
    /* Meer padding voor mobiel om ruimte te maken voor het uitgeklapte menu */
    padding-top: 180px;
  }
  
  .sticky-tabs {
    top: 125px; 
  }
  
  /* Wanneer het menu is uitgeklapt, pas de padding aan */
  body.menu-expanded {
    padding-top: 300px; /* Meer ruimte voor uitgeklapt menu */
  }
}

.toast-container-top-left {
  position: fixed;
  top: 5rem;
  left: 1rem;
  z-index: 1055;
}

.custom-purple-btn {
  background-color: purple;
  color: white;
}
.custom-purple-btn:hover {
  background-color: darkpurple;
}
</style>
